import React, { useEffect, useState, useMemo } from "react"
import cx from "classnames"
import VizSensor from "react-visibility-sensor"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "gatsby"
import { getSrc } from "gatsby-plugin-image"
import { useSiteTranslations } from "@hooks/useSiteTranslations"
import { Cta } from "@components/common/cta/Cta"
import { AddToCartButton } from "../add-to-cart-button/AddToCartButton"

import { getVariant } from "./util.ts"
import { addLineItemsToCart } from "@ducks/cartSlice"
import formatPrice from "@utils/formatPrice"
import pricePercent from "@utils/pricePercent"
import {
  toLocaliseShopifyProductData,
  toLocaliseShopifyVariantData,
} from "@utils/toLocaliseShopifyData"
import trackGtagEvent from "@utils/trackGtagEvent"

import * as s from "./ProductCardV3.module.scss"

export const ProductCardV3 = ({ product, className, hasAddToCardButton }) => {
  const dispatch = useDispatch()
  const cart = useSelector(state => state.cart)
  const [selectedVariant, setSelectedVariant] = useState(getVariant(product))
  const [isVisible, setIsVisible] = useState(false)
  const [addedToCart, setAddedToCart] = useState(false)
  const {
    shopTranslations: { shop },
  } = useSiteTranslations()
  const localisedProductData = toLocaliseShopifyProductData(product)
  const localisedVariantData = useMemo(
    () => toLocaliseShopifyVariantData(selectedVariant),
    [selectedVariant],
  )

  useEffect(() => {
    setSelectedVariant(getVariant(product))
  }, [product])

  const onChangeVisibility = isActive => {
    if (isActive) {
      setIsVisible(true)
    }
  }

  const encodeShopifyProductId = id => {
    const encoded = btoa(id)
    return encoded
  }

  const handleAddToCart = () => {
    const cartId = cart.cart.id

    dispatch(
      addLineItemsToCart({
        product: {
          shopifyId: encodeShopifyProductId(
            `gid://shopify/ProductVariant/${product.variants[0].id}`,
          ),
          quantity: 1,
        },
        cartId,
      }),
    )
    setAddedToCart(true)

    trackGtagEvent(
      "add_to_cart",
      selectedVariant.contextualPricing.price.amount,
      [{ item_id: selectedVariant.id, item_name: selectedVariant.name }],
      selectedVariant.contextualPricing.price.currencyCode,
    )
  }

  const handleCtaLink = () => {
    trackGtagEvent(
      "view_item",
      selectedVariant.contextualPricing.price.amount,
      [
        {
          item_id: selectedVariant.id,
          item_name: selectedVariant.name,
        },
      ],
      selectedVariant.contextualPricing.price.currencyCode,
    )
  }

  const ProductCarousel = ({ variants, handle }) => {
    const productImage = product.media[0]?.image
      ? getSrc(product.media[0].image.gatsbyImageData)
      : product.media?.[0]?.src ||
        getSrc(selectedVariant.image?.gatsbyImageData)

    return (
      <div className={s.ProductCarousel}>
        {variants.map((variant, i) => (
          <Link to={`/shop/${handle}`} key={i}>
            {productImage && (
              <div
                className={cx(s.ProductCarousel__varaint, {
                  [s.ProductCarousel__varaint___active]:
                    selectedVariant === variant,
                })}
                aria-label={
                  selectedVariant.image?.altText || localisedProductData.title
                }
                role="image"
                style={{
                  backgroundImage: `url(${productImage})`,
                }}
              />
            )}
          </Link>
        ))}
      </div>
    )
  }

  const PriceDisplay = () => (
    <h4 className={s.ProductCard__price}>
      {localisedVariantData.compareAtPrice ? (
        <>
          <span className={s.ProductCard__pricePercent}>
            {pricePercent(selectedVariant)} {shop.compareAtPrice.percentOff}
          </span>
          <span className={s.ProductCard__priceCurrent}>
            {shop.compareAtPrice.now}{" "}
            {product.variants.length > 1 && shop.compareAtPrice.from}{" "}
            {formatPrice(
              localisedVariantData.price,
              localisedVariantData.priceCurrencyCode,
            )}
          </span>
          <span className={s.ProductCard__pricePrevious}>
            {shop.compareAtPrice.was}{" "}
            {formatPrice(
              localisedVariantData.compareAtPrice,
              localisedVariantData.compareAtPriceCurrencyCode,
            )}
          </span>
        </>
      ) : (
        formatPrice(
          localisedVariantData.price,
          localisedVariantData.priceCurrencyCode,
        )
      )}
    </h4>
  )

  return (
    <div
      className={`${s.ProductCard} ${className && className} ${
        isVisible && s.ProductCard___visible
      }`}
    >
      {selectedVariant && (
        <VizSensor partialVisibility onChange={e => onChangeVisibility(e)}>
          <>
            <ProductCarousel
              variants={product.variants}
              handle={product.handle}
            />

            <div className={s.ProductCard__productMeta}>
              <div className={s.ProductCard__title}>
                <h4>{localisedProductData.title}</h4>
              </div>

              <div className={s.ProductCard__PriceRow}>
                <PriceDisplay />

                <div>
                  <Cta
                    title={shop.seeMoreCtaText}
                    link={`/shop/${product.handle}`}
                    onClick={handleCtaLink}
                  />

                  {hasAddToCardButton !== false && (
                    <AddToCartButton
                      className={s.ProductCard__addToCartButton}
                      onClick={handleAddToCart}
                      title={shop.addToCartCtaText}
                      addedTitle={shop.addedToCartCtaText}
                      added={addedToCart}
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        </VizSensor>
      )}
    </div>
  )
}
